import { ImageFragment } from '@/graphql/fragments/image';
import { LearnFragment } from '@/graphql/fragments/learn';
import { gql } from '@apollo/client';

export const QUERY_COURSE = gql`
  query course($slug: [String]) {
    courses(pagination: { limit: 1 }, filters: { slug: { in: $slug } }, publicationState: LIVE) {
      data {
        id
        attributes {
          title
          slug
          description
          duration
          textContent {
            markdownText
          }
          course_category {
            data {
              attributes {
                name
                description
              }
            }
          }
          courseCompletedSection {
            title
            message
          }
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          hero {
            data {
              ...ImageFragment
            }
          }
          modules {
            id
            title
            description
            lessons {
              id
              title
              description
              duration
              textContent {
                markdownText
              }
              thumbnail {
                data {
                  ...ImageFragment
                }
              }
              authors {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
          type {
            data {
              attributes {
                name
              }
            }
          }
        }
      }
    }
  }
  ${ImageFragment}
`;

export const QUERY_COURSE_LESSON = gql`
  query courseLesson($slug: [String]) {
    courses(pagination: { limit: 1 }, filters: { slug: { in: $slug } }, publicationState: LIVE) {
      data {
        id
        attributes {
          title
          slug
          duration
          description
          feedbackQuestions {
            question
          }
          textContent {
            markdownText
          }
          course_category {
            data {
              attributes {
                name
                description
              }
            }
          }
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          hero {
            data {
              ...ImageFragment
            }
          }
          modules {
            id
            title
            description
            lessons {
              id
              duration
              title
              description
              textContent {
                markdownText
              }
              objectivesSection {
                id
                title
                objectives {
                  description
                }
              }
              thumbnail {
                data {
                  ...ImageFragment
                }
              }
              highlightContent {
                audio {
                  audio {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                  transcription
                }
                embedded {
                  link
                  provider
                }
                image {
                  media {
                    data {
                      attributes {
                        url
                        alternativeText
                        mime
                      }
                    }
                  }
                }
              }
              downloadableContent {
                title
                description
                content {
                  data {
                    attributes {
                      url
                    }
                  }
                }
              }
              faq {
                title
                description
              }
              authors {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
          type {
            data {
              attributes {
                name
              }
            }
          }
        }
      }
    }
  }
  ${ImageFragment}
`;

export const QUERY_FETCH_LATEST_COURSES = gql`
  query fetchLatestCourses($slug: [String]) {
    courses(pagination: { limit: 2 }, filters: { slug: { notIn: $slug } }, publicationState: LIVE) {
      data {
        id
        attributes {
          title
          slug
          description
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          modules {
            id
            lessons {
              id
              authors {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
          type {
            data {
              attributes {
                name
              }
            }
          }
        }
      }
    }
  }
  ${ImageFragment}
`;

export const FETCH_COURSES = gql`
  query fetchCourses($limit: Int, $publishedAt: [String] = "publishedAt:asc") {
    courses(pagination: { limit: $limit }, publicationState: LIVE, sort: $publishedAt) {
      data {
        id
        attributes {
          title
          slug
          duration
          description
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          type {
            data {
              attributes {
                name
              }
            }
          }
          course_category {
            data {
              attributes {
                name
                description
              }
            }
          }
          modules {
            id
            lessons {
              id
              authors {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${ImageFragment}
`;

export const FETCH_COURSES_PAGEABLE = gql`
  query fetchCoursesPageable($page: Int!, $search: String, $theme: String, $topic: String) {
    courses(
      pagination: { pageSize: 8, page: $page }
      filters: { title: { containsi: $search }, themes: { theme: { eq: $theme }, topic: { topic: { eq: $topic } } } }
      publicationState: LIVE
    ) {
      data {
        id
        attributes {
          title
          slug
          description
          duration
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          type {
            data {
              attributes {
                name
              }
            }
          }
          course_category {
            data {
              attributes {
                name
                description
              }
            }
          }
          modules {
            id
            lessons {
              id
              authors {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      meta {
        pagination {
          page
          pageCount
          total
        }
      }
    }
  }
  ${ImageFragment}
`;

export const FETCH_LEARN = gql`
  query fetchLearn($idUser: ID!, $completed: Boolean) {
    learn: learns(filters: { users_permissions_user: { id: { eq: $idUser } }, completed: { eq: $completed } }) {
      data {
        id
        attributes {
          ...LearnFragment
        }
      }
    }
  }
  ${LearnFragment}
`;

export const FETCH_COURSE_LEARN = gql`
  query fetchCourseLearn($idUser: ID!, $courseSlug: String!) {
    learn: learns(filters: { users_permissions_user: { id: { eq: $idUser } }, course: { slug: { eq: $courseSlug } } }) {
      data {
        id
        attributes {
          ...LearnFragment
        }
      }
    }
  }
  ${LearnFragment}
`;

export const QUERY_COURSE_PAGE = gql`
  query CoursePage {
    coursePage {
      data {
        attributes {
          lesson {
            beginButtonLabel
            watchAgainButtonlabel
            lockedButtonlabel
          }
        }
      }
    }
  }
`;
